import React, { useCallback, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Grid from "@mui/material/Grid";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import "@brainhubeu/react-carousel/lib/style.css";
import { useTheme } from "@mui/material";
import { arrowsPlugin, Dots } from "@brainhubeu/react-carousel";
import { ICarouselProps } from "./types";

const ReactCarousel = dynamic(() => import("@brainhubeu/react-carousel"), {
  ssr: false,
});

const Carousel: React.FC<ICarouselProps> = ({
  children,
  showButtons,
  showDots,
  carouselProps,
  dotsProps,
  plugins,
}) => {
  const theme = useTheme();

  // react-carousel doesn't have callbacks for custom buttons.
  // We'll use these hidden buttons instead
  const leftBtnRef = useRef<HTMLButtonElement | null>(null);
  const rightBtnRef = useRef<HTMLButtonElement | null>(null);
  // const rightBtnRefDisabled = useRef<HTMLButtonElement | null>(null);
  // const leftBtnRefDisabled = useRef<HTMLButtonElement | null>(null);

  const [rightBtnRefDisabled, setRightBtnRefDisabled] = useState(null);
  const [leftBtnRefDisabled, setLeftBtnRefDisabled] = useState(null);

  const onLeftBtnRefChange = useCallback((node: any) => {
    // ref value changed to node
    setLeftBtnRefDisabled(node); // e.g. change ref state to trigger re-render
  }, []);

  const onRightBtnRefChange = useCallback((node: any) => {
    // ref value changed to node
    setRightBtnRefDisabled(node); // e.g. change ref state to trigger re-render
  }, []);

  const [currentSlide, setCurrentSlide] = useState(0);

  const onSlidechange = (value: any) => {
    setCurrentSlide(value);
  };
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ReactCarousel
            onChange={onSlidechange}
            value={currentSlide}
            plugins={[
              "fastSwipe",
              {
                resolve: arrowsPlugin,
                options: {
                  arrowLeft: (
                    <button hidden ref={leftBtnRef}>
                      Left
                    </button>
                  ),
                  arrowRight: (
                    <button hidden ref={rightBtnRef}>
                      Right
                    </button>
                  ),
                  arrowRightDisabled: (
                    <button hidden ref={onRightBtnRefChange}></button>
                  ),
                  arrowLeftDisabled: (
                    <button hidden ref={onLeftBtnRefChange}></button>
                  ),
                  addArrowClickHandler: true,
                },
              },
              ...(plugins || []),
            ]}
            {...(carouselProps || {})}
          >
            {children}
          </ReactCarousel>
        </Grid>
        {showButtons && (
          <Grid item xs={12}>
            <Box
              display="flex"
              width="100%"
              justifyContent="center"
              gap={10}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <IconButton
                sx={{
                  color: "white",
                  background: theme.palette.primary.main,
                  marginLeft: theme.spacing(4),
                  marginRight: theme.spacing(4),
                  marginTop: theme.spacing(2),
                  marginBottom: theme.spacing(2),
                  fontSize: theme.spacing(12),
                  height: theme.spacing(12),
                  width: theme.spacing(12),
                  "&:hover": {
                    background: theme.palette.primary.main,
                    color: "white",
                  },
                  [theme.breakpoints.down("sm")]: {
                    fontSize: theme.spacing(7),
                    height: theme.spacing(7),
                    width: theme.spacing(7),
                  },
                  "&.Mui-disabled": {
                    background: "#CCCCCC",
                    color: "white",
                    "&:hover": {
                      background: "#CCCCCC",
                      color: "white",
                    },
                  },
                }}
                disabled={leftBtnRefDisabled ? true : false}
                onClick={() => leftBtnRef.current?.click()}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>
              <IconButton
                sx={{
                  color: "white",
                  background: theme.palette.primary.main,
                  marginLeft: theme.spacing(4),
                  marginRight: theme.spacing(4),
                  marginTop: theme.spacing(2),
                  marginBottom: theme.spacing(2),
                  fontSize: theme.spacing(12),
                  height: theme.spacing(12),
                  width: theme.spacing(12),
                  "&:hover": {
                    background: theme.palette.primary.main,
                    color: "white",
                  },
                  [theme.breakpoints.down("sm")]: {
                    fontSize: theme.spacing(7),
                    height: theme.spacing(7),
                    width: theme.spacing(7),
                  },
                  "&.Mui-disabled": {
                    background: "#CCCCCC",
                    color: "white",
                    "&:hover": {
                      background: "#CCCCCC",
                      color: "white",
                    },
                  },
                }}
                disabled={rightBtnRefDisabled ? true : false}
                onClick={() => rightBtnRef.current?.click()}
              >
                <ChevronRightRoundedIcon />
              </IconButton>
            </Box>
          </Grid>
        )}
        {showDots && (
          <Grid item xs={12}>
            <Dots
              value={currentSlide}
              onChange={onSlidechange}
              {...dotsProps}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default Carousel;
